import { format } from "date-fns";
import type { EventInput } from "@fullcalendar/core";
import { Temporal } from "temporal-polyfill";
import { parseISOToDate, parseISOToMs } from "@/lib/date-utils.ts";
import type { CalendarContext } from "@/types/threadEvents.ts";
import { buildCalendarUrl, type CalendarLinkRange, timeRangeToLinkRange } from "./calendar-url.ts";

export interface TimeRange {
  id: string;
  startISO: string;
  endISO: string;
  allDay: boolean;
}

export interface MatchedEvent {
  urn: string;
  title: string;
  startISO: string;
  endISO: string;
  rangeId: string;
}

function rangeId(startISO: string, endISO: string) {
  return `${startISO}__${endISO}`;
}

/** Format a time, dropping :00 minutes for clean display: "6pm" or "5:30pm" */
function formatShortTime(date: Date): string {
  const h = date.getHours() % 12 || 12;
  const m = date.getMinutes();
  const meridiem = date.getHours() >= 12 ? "pm" : "am";
  return m === 0 ? `${h}${meridiem}` : `${h}:${String(m).padStart(2, "0")}${meridiem}`;
}

/**
 * Merge adjacent (touching boundaries) or overlapping time ranges into
 * single continuous ranges. Fully contained ranges are silently absorbed.
 */
function mergeRanges(ranges: TimeRange[]): TimeRange[] {
  if (ranges.length <= 1) return ranges;
  const sorted = [...ranges].sort((a, b) => parseISOToMs(a.startISO) - parseISOToMs(b.startISO));
  const merged: TimeRange[] = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    const prev = merged[merged.length - 1];
    const prevEnd = parseISOToMs(prev.endISO);
    const curStart = parseISOToMs(sorted[i].startISO);
    const curEnd = parseISOToMs(sorted[i].endISO);
    // Adjacent (end === start) or overlapping — merge into one.
    // When curEnd <= prevEnd the current range is fully contained and silently absorbed.
    if (curStart <= prevEnd) {
      if (curEnd > prevEnd) {
        const endISO = sorted[i].endISO;
        merged[merged.length - 1] = {
          id: rangeId(prev.startISO, endISO),
          startISO: prev.startISO,
          endISO,
          allDay: prev.allDay,
        };
      }
    } else {
      merged.push(sorted[i]);
    }
  }
  return merged;
}

/**
 * Toggle selection logic: if the new range overlaps existing ranges, subtract it
 * (punch holes). If it doesn't overlap anything, add it and merge adjacent ranges.
 */
export function toggleTimeRanges(
  existing: TimeRange[],
  newStart: Date,
  newEnd: Date,
  allDay: boolean,
): TimeRange[] {
  const newStartMs = newStart.getTime();
  const newEndMs = newEnd.getTime();

  const hasOverlap = existing.some((r) => {
    const rStart = parseISOToMs(r.startISO);
    const rEnd = parseISOToMs(r.endISO);
    return newStartMs < rEnd && newEndMs > rStart;
  });

  if (!hasOverlap) {
    const startISO = newStart.toISOString();
    const endISO = newEnd.toISOString();
    return mergeRanges([...existing, { id: rangeId(startISO, endISO), startISO, endISO, allDay }]);
  }

  // Subtract the new selection from existing ranges
  const result: TimeRange[] = [];
  for (const r of existing) {
    const rStart = parseISOToMs(r.startISO);
    const rEnd = parseISOToMs(r.endISO);

    if (newStartMs >= rEnd || newEndMs <= rStart) {
      result.push(r);
    } else {
      if (rStart < newStartMs) {
        const startISO = r.startISO;
        const endISO = newStart.toISOString();
        result.push({ id: rangeId(startISO, endISO), startISO, endISO, allDay: r.allDay });
      }
      if (rEnd > newEndMs) {
        const startISO = newEnd.toISOString();
        const endISO = r.endISO;
        result.push({ id: rangeId(startISO, endISO), startISO, endISO, allDay: r.allDay });
      }
    }
  }
  return result;
}

/** Normalize a date span so the earlier date is always first. */
export function normalizeSpan(a: Date, b: Date): [start: Date, end: Date] {
  return a <= b ? [a, b] : [b, a];
}

/**
 * Iterate over the rectangular grid of day cells for a month-view selection.
 * Computes day-of-week column bounds and week rows from start/end, then calls
 * `callback` for each cell in the rectangle. Used by both `decomposeBoxSelection`
 * (to build ranges) and the drag highlight (to add CSS classes to day cells).
 *
 * @param start - Selection start (midnight, inclusive)
 * @param end - Selection end (midnight, exclusive — FullCalendar convention)
 * @param callback - Called with each day's Date in the rectangle
 */
export function iterateMonthGridCells(start: Date, end: Date, callback: (day: Date) => void): void {
  // FullCalendar's exclusive end: last selected day is end - 1
  const lastDay = new Date(end.getFullYear(), end.getMonth(), end.getDate() - 1);
  const minCol = Math.min(start.getDay(), lastDay.getDay());
  const maxCol = Math.max(start.getDay(), lastDay.getDay());

  // Find the Sunday of each week row
  const startSunday = new Date(start);
  startSunday.setDate(startSunday.getDate() - startSunday.getDay());
  const lastSunday = new Date(lastDay);
  lastSunday.setDate(lastSunday.getDate() - lastSunday.getDay());

  const week = new Date(startSunday);
  while (week <= lastSunday) {
    for (let col = minCol; col <= maxCol; col++) {
      callback(new Date(week.getFullYear(), week.getMonth(), week.getDate() + col));
    }
    week.setDate(week.getDate() + 7);
  }
}

/**
 * Decompose a potentially cross-day selection into per-day ranges using
 * rectangular "spreadsheet" box selection logic.
 *
 * **Time grid (day/week view):** The box spans the same time-of-day window
 * across each day column. Mon 9am → Wed 3pm returns:
 *   Mon 9am–3pm, Tue 9am–3pm, Wed 9am–3pm
 *
 * **Day grid (month view):** Both times are midnight, so the box spans the
 * same day-of-week columns across week rows. Wed Apr 8 → Fri Apr 17 returns:
 *   Wed 8, Thu 9, Fri 10, Wed 15, Thu 16, Fri 17
 * (not every day from 8 through 17). This lets users select e.g. "all Mondays"
 * or "Mon–Wed for 3 weeks" by dragging a rectangle on the month grid.
 */
export function decomposeBoxSelection(
  rawStart: Date,
  rawEnd: Date,
  allDay: boolean,
): { start: Date; end: Date }[] {
  const [start, end] = normalizeSpan(rawStart, rawEnd);

  // Same day — normalize time order and return
  if (start.toDateString() === end.toDateString()) {
    return [{ start, end }];
  }

  const startTOD = start.getHours() * 60 + start.getMinutes();
  const endTOD = end.getHours() * 60 + end.getMinutes();

  // Full-day selection (month view): use FullCalendar's allDay flag instead of
  // midnight heuristic to avoid false positives from timegrid midnight-to-midnight drags.
  // Rectangular grid decomposition — same day-of-week columns across week rows.
  if (allDay) {
    const ranges: { start: Date; end: Date }[] = [];
    iterateMonthGridCells(start, end, (day) => {
      const nextDay = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);
      ranges.push({ start: day, end: nextDay });
    });
    return ranges;
  }

  // Time-based selection (week/day view): same time window on each day column.
  // Midnight on a different day means "end of previous day" (exclusive end).
  // Safe: same-day spans return early above, so endTOD === 0 here always means
  // a cross-day boundary. Matches computeDragBoxes' explicit !sameDay guard.
  const effectiveEndTOD = endTOD === 0 ? 1440 : endTOD;
  const topMin = Math.min(startTOD, effectiveEndTOD);
  const botMin = Math.max(startTOD, effectiveEndTOD);

  const firstDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const rawLastDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const lastDay =
    endTOD === 0
      ? new Date(rawLastDay.getFullYear(), rawLastDay.getMonth(), rawLastDay.getDate() - 1)
      : rawLastDay;

  const ranges: { start: Date; end: Date }[] = [];
  const d = new Date(firstDay);
  while (d <= lastDay) {
    const rangeStart = new Date(
      d.getFullYear(),
      d.getMonth(),
      d.getDate(),
      Math.floor(topMin / 60),
      topMin % 60,
    );
    const rangeEnd =
      botMin === 1440
        ? new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 0, 0)
        : new Date(
            d.getFullYear(),
            d.getMonth(),
            d.getDate(),
            Math.floor(botMin / 60),
            botMin % 60,
          );
    ranges.push({ start: rangeStart, end: rangeEnd });
    d.setDate(d.getDate() + 1);
  }
  return ranges;
}

/** Format a single range into chip text like "Apr 30 2-4pm" or "Apr 30 9am - May 1 10am" */
export function formatChipLabel(range: TimeRange): string {
  const start = parseISOToDate(range.startISO);
  const end = parseISOToDate(range.endISO);
  const startDay = format(start, "MMM d");
  const endDay = format(end, "MMM d");
  if (startDay === endDay) {
    return `${startDay} ${formatShortTime(start)}-${formatShortTime(end)}`;
  }
  return `${startDay} ${formatShortTime(start)} - ${endDay} ${formatShortTime(end)}`;
}

/** Filter calendar events whose time overlaps any selected range. */
export function findEventsInRanges(
  calendarEvents: EventInput[],
  ranges: TimeRange[],
): MatchedEvent[] {
  const results: MatchedEvent[] = [];

  for (const range of ranges) {
    const rStart = parseISOToMs(range.startISO);
    const rEnd = parseISOToMs(range.endISO);

    for (const event of calendarEvents) {
      if (!event.start || !event.end || !event.id) continue;
      const eStartISO =
        event.start instanceof Date ? event.start.toISOString() : String(event.start);
      const eEndISO = event.end
        ? event.end instanceof Date
          ? event.end.toISOString()
          : String(event.end)
        : eStartISO;
      const eStart = new Date(eStartISO).getTime();
      const eEnd = new Date(eEndISO).getTime();

      if (eStart < rEnd && eEnd > rStart) {
        // FullCalendar event.id is the source-ent id (see useCalendarEvents). Wrap it
        // as a canonical URN so the agent can resolve it via parseEventIdentifier.
        const sourceEntId = String(event.id);
        results.push({
          urn: sourceEntId.startsWith("urn:") ? sourceEntId : `urn:bb:source:${sourceEntId}`,
          title: String(event.title ?? ""),
          startISO: eStartISO,
          endISO: eEndISO,
          rangeId: range.id,
        });
      }
    }
  }

  return results;
}

/**
 * Emit an ISO 8601 string tagged with the user's IANA timezone, e.g.
 * "2026-04-30T14:00:00-07:00[America/Los_Angeles]". Unambiguous for both
 * humans and LLMs, and round-trippable by `parseISOToMs` (Temporal).
 *
 * All-day calendar events surface from FullCalendar as bare dates
 * ("2026-04-23"); `Temporal.Instant.from` rejects those, so promote to
 * start-of-day in the user's timezone instead. Without this the implicit
 * calendar enrichment throws inside `buildMessage` and the omnibox
 * submission silently fails (BIG-4180).
 */
export function toZonedISO(iso: string, timezone: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    return Temporal.PlainDate.from(iso).toZonedDateTime(timezone).toString();
  }
  return Temporal.Instant.from(iso).toZonedDateTimeISO(timezone).toString();
}

/**
 * Group ranges by their local wall-clock date in the user's IANA zone. Ranges
 * landing on the same day end up together so `buildTimeRangeMessage` can emit
 * one consolidated markdown link per day (instead of one per range).
 */
function groupRangesByDay(
  ranges: TimeRange[],
  timezone: string,
): Array<{ date: string; links: CalendarLinkRange[]; originalIndices: number[] }> {
  const groups = new Map<
    string,
    { date: string; links: CalendarLinkRange[]; originalIndices: number[] }
  >();
  ranges.forEach((range, idx) => {
    const link = timeRangeToLinkRange(range, timezone);
    const existing = groups.get(link.date);
    if (existing) {
      existing.links.push(link);
      existing.originalIndices.push(idx);
    } else {
      groups.set(link.date, { date: link.date, links: [link], originalIndices: [idx] });
    }
  });
  return Array.from(groups.values());
}

/**
 * Build the user-visible bubble content (markdown with clickable chip links).
 * `timezone` is the user's IANA zone; chip labels render in that wall-clock
 * frame so they match the agent's view.
 *
 * Event-level detail used to ride along here as a serialized `agentContent`
 * JSON, but past a certain calendar density it blew past the wire cap
 * (BIG-4941). The structured payload now travels as `calendarContext`
 * (see `buildTimeRangeCalendarContext`) and the server fetches events
 * itself — keeping this function purely about the bubble.
 */
export function buildTimeRangeMessage(
  instruction: string,
  ranges: TimeRange[],
  timezone: string,
): { content: string } {
  const lines: string[] = [instruction.trim()];

  if (ranges.length > 0) {
    const label = ranges.length === 1 ? "Time range" : "Time ranges";
    // One link per day — ranges sharing a day share one href that pre-selects
    // all of them on open. Chip labels stay per-range so the user reads each
    // range individually, but adjacent chips on the same day share a target.
    const groups = groupRangesByDay(ranges, timezone);
    const hrefByRangeIndex = new Map<number, string>();
    for (const group of groups) {
      const href = buildCalendarUrl({ ranges: group.links });
      for (const origIdx of group.originalIndices) hrefByRangeIndex.set(origIdx, href);
    }
    const linkList = ranges
      .map((r, idx) => `[${formatChipLabel(r)}](${hrefByRangeIndex.get(idx)})`)
      .join(", ");
    lines.push("");
    lines.push(`${label}: ${linkList}`);
  }
  return { content: lines.join("\n") };
}

/**
 * Compact, wire-bounded description of the user's selected time ranges. The
 * server expands this into the actor's JSON view by fetching events itself
 * (`api/src/handlers/calendarContextExpansion.ts`). Chip labels travel along
 * so the actor's rendered ranges read the same as the user-visible chips.
 */
export function buildTimeRangeCalendarContext(
  instruction: string,
  ranges: TimeRange[],
  timezone: string,
): CalendarContext {
  return {
    kind: "selectedTimeRanges",
    instruction: instruction.trim(),
    timezone,
    ranges: ranges.map((r) => ({
      startISO: r.startISO,
      endISO: r.endISO,
      label: formatChipLabel(r),
    })),
  };
}
